import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth, apiErr } from "./AdminAuth";
import { usePublicData } from "@/hooks/usePublicData";
import { resolveSite } from "@/lib/siteConfig";
import SiteLogo from "@/components/SiteLogo";

export default function AdminLogin() {
  const { login } = useAdminAuth();
  const { settings } = usePublicData();
  const site = resolveSite(settings);
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      const dest = loc.state?.from?.pathname || "/dekosta-yonetim";
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-site grid place-items-center p-6 bg-grain">
      <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
      <div className="relative w-full max-w-md border border-white/10 bg-surface-2 p-10">
        <div className="mb-8">
          <SiteLogo site={site} size="md" textClassName="text-lg" />
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-2">Yönetim Paneli</div>
        </div>
        <h1 className="font-display text-3xl font-black uppercase tracking-tight mb-2">Giriş</h1>
        <p className="text-sm text-zinc-400 mb-8">Admin hesabınızla oturum açın.</p>

        <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 block">E-posta</span>
            <div className="relative">
              <Mail className="w-4 h-4 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="w-full bg-surface-1 border border-white/10 text-white placeholder:text-zinc-500 focus:border-[#FF4F00] focus:outline-none font-mono text-sm h-12 pl-10 pr-4"
                placeholder="admin@dekosta3d.com"
              />
            </div>
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 block">Şifre</span>
            <div className="relative">
              <Lock className="w-4 h-4 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                className="w-full bg-surface-1 border border-white/10 text-white placeholder:text-zinc-500 focus:border-[#FF4F00] focus:outline-none font-mono text-sm h-12 pl-10 pr-4"
                placeholder="••••••••"
              />
            </div>
          </label>
          <button
            type="submit" disabled={busy}
            data-testid="admin-login-submit"
            className="w-full bg-[#FF4F00] hover:bg-[#E64700] disabled:opacity-50 text-black font-bold uppercase tracking-wider text-xs h-12 flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Giriş Yapılıyor…</> : "Giriş Yap"}
          </button>
        </form>
      </div>
    </div>
  );
}
